import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import isRequiredIf from 'react-proptype-conditional-require';
import PropTypes from 'prop-types';

import Button from '../Button';
import withDeprecatedProps, { DeprTypes } from '../withDeprecatedProps';
import CheckBox from '../CheckBox';

import EmptyState from '../EmptyState';
import Spinner from '../Spinner';

// Constants
const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
};

const DEFAULT_SORT_BUTTONS_SCREEN_READER_TEXT = {
  asc: 'sort ascending',
  desc: 'sort descending',
  defaultText: 'click to sort',
};

// Utility functions
const sortData = (data, columnKey, direction) => {
  return [...data].sort((a, b) => {
    const directionIsAsc = direction === SORT_DIRECTIONS.ASC;
    if (a[columnKey] > b[columnKey]) return directionIsAsc ? 1 : -1;
    if (a[columnKey] < b[columnKey]) return directionIsAsc ? -1 : 1;
    return 0;
  });
};

const getSortIcon = (sortDirection) => {
  const sortIconClassName = {
    'ri-arrow-up-s-fill': sortDirection === SORT_DIRECTIONS.ASC,
    'ri-arrow-down-s-fill': sortDirection === SORT_DIRECTIONS.DESC,
    'ri-expand-up-down-fill': !sortDirection,
  };
  return <span className={classNames(sortIconClassName)} />;
};

function Table({
  data,
  paginatedData,
  isSelectable,
  selected,
  setSelected,
  showRows,
  tableSortable,
  defaultSortedColumn,
  defaultSortDirection,
  caption,
  sortButtonsScreenReaderText = DEFAULT_SORT_BUTTONS_SCREEN_READER_TEXT,
  columns,
  hasFixedColumnWidths,
  headingClassName = [],
  hideHeader,
  className,
  rowHeaderColumnKey,
  isLoading
}) {
  const [sortedColumn, setSortedColumn] = useState(tableSortable ? defaultSortedColumn : '');
  const [sortDirection, setSortDirection] = useState(tableSortable ? defaultSortDirection : '');

  useEffect(() => {
    if (tableSortable && defaultSortedColumn) {
      setSortedColumn(defaultSortedColumn);
    }
  }, [defaultSortedColumn, tableSortable]);

  const handleSort = (columnKey) => {
    const newDirection = sortedColumn === columnKey
      ? (sortDirection === SORT_DIRECTIONS.DESC ? SORT_DIRECTIONS.ASC : SORT_DIRECTIONS.DESC)
      : SORT_DIRECTIONS.DESC;

    setSortedColumn(columnKey);
    setSortDirection(newDirection);
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setSelected(checked ? data.map((row) => row.id) : []);
  };

  const handleSelect = (event) => {
    const { checked, name } = event.target;
    // Convert to number if it's a numeric string, otherwise keep as string for UUIDs
    const id = !isNaN(name) ? Number(name) : name;

    setSelected(prev =>
      checked
        ? prev.filter(selectedId => selectedId !== id)
        : [...new Set([...prev, id])]
    );
  };

  const handleRowClick = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const getSortButtonScreenReaderText = (columnKey) => {
    return sortedColumn === columnKey
      ? sortButtonsScreenReaderText[sortDirection]
      : sortButtonsScreenReaderText.defaultText;
  };

  const getTableHeading = (column) => {
    if (tableSortable && column.columnSortable) {
      return (
        <Button variant="header" onClick={() => handleSort(column.key)}>
          <span>
            {column.label}
            <span className="sr-only" data-testid="table-heading-sr">
              {' '}{getSortButtonScreenReaderText(column.key)}{' '}
            </span>
            {getSortIcon(column.key === sortedColumn ? sortDirection : '')}
          </span>
        </Button>
      );
    }

    if (column.hideHeader) {
      return (
        <span className="sr-only" data-testid="table-heading-sr">
          {column.label}
        </span>
      );
    }

    return column.label;
  };

  const renderTableHeadings = () => (
    <thead className={classNames(...headingClassName, { 'd-inline': hasFixedColumnWidths })}>
      <tr className={classNames({ 'd-flex': hasFixedColumnWidths })}>
        {isSelectable && (
          <th className="table-checkbox-column">
            <CheckBox
              id="selectAll"
              checked={selected.length === data.length}
              indeterminate={selected.length > 0 && selected.length < data.length}
              onChange={handleSelectAll}
              className="table-checkbox"
              value="selectAll"
            />
          </th>
        )}
        {columns.map((col, index) => (
          <th
            key={col.key}
            scope="col"
            className={classNames({
              sortable: tableSortable && col.columnSortable,
              [col.width]: hasFixedColumnWidths,
              'table-is-selectable': isSelectable && index === 0,
            })}
          >
            {getTableHeading(col)}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    const sortedData = tableSortable ? sortData(data, sortedColumn, sortDirection) : data;
    const displayData = (paginatedData || sortedData).slice(0, showRows);

    if (data.length === 0) {
      return (
        <td colSpan={isSelectable ? columns.length + 1 : columns.length}>
          {isLoading ? (
            <div className="table-loading-wrapper">
              <Spinner animation="border" size="lg" variant="gray" />
            </div>
          ) : (
            <EmptyState className="mt-5" type="box" message="No data found" />
          )}
        </td>
      );
    }

    return displayData.map((row) => (
      <tr
        key={row.id}
        className={classNames({
          'd-flex': hasFixedColumnWidths,
          'cursor-pointer': isSelectable,
          selected: isSelectable && selected.includes(row.id)
        })}
        onClick={() => isSelectable && handleRowClick(row.id)}
      >
        {isSelectable && (
          <td className="table-checkbox-column">
            <CheckBox
              id={row.id}
              checked={selected.includes(row.id)}
              onChange={handleSelect}
              name={row.id.toString()}
              className="table-checkbox"
              value={row.id.toString()}
            />
          </td>
        )}
        {columns.map(({ key, width, render, className: columnClassName }, index) => {
          const isRowHeader = key === rowHeaderColumnKey;
          return React.createElement(
            isRowHeader ? 'th' : 'td',
            {
              key,
              className: classNames(
                {
                  [width]: hasFixedColumnWidths,
                  'table-is-selectable': isSelectable && index === 0,
                },
                columnClassName
              ),
              scope: isRowHeader ? 'row' : null,
              children: render ? render({ value: row[key], row }) : row[key],
            }
          );
        })}
      </tr>
    ));
  };

  return (
    <table className={classNames('table', className)}>
      {caption && <caption>{caption}</caption>}
      {!hideHeader && renderTableHeadings()}
      <tbody>{renderTableBody()}</tbody>
    </table>
  );
}

Table.propTypes = {
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  className: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  paginatedData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    columnSortable: isRequiredIf(PropTypes.bool, props => props.tableSortable),
    render: PropTypes.func,
    hideHeader: PropTypes.bool,
    width: isRequiredIf(PropTypes.string, props => props.hasFixedColumnWidths),
    className: PropTypes.string,
  })).isRequired,
  hideHeader: PropTypes.bool,
  headingClassName: PropTypes.arrayOf(PropTypes.string),
  tableSortable: PropTypes.bool,
  hasFixedColumnWidths: PropTypes.bool,
  defaultSortedColumn: isRequiredIf(PropTypes.string, props => props.tableSortable),
  defaultSortDirection: isRequiredIf(PropTypes.string, props => props.tableSortable),
  sortButtonsScreenReaderText: isRequiredIf(
    PropTypes.shape({
      asc: PropTypes.string,
      desc: PropTypes.string,
      defaultText: PropTypes.string,
    }),
    props => props.tableSortable,
  ),
  rowHeaderColumnKey: PropTypes.string,
  isSelectable: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.string),
  setSelected: PropTypes.func,
  isLoading: PropTypes.bool,
  showRows: PropTypes.number
};

Table.defaultProps = {
  caption: null,
  className: undefined,
  headingClassName: [],
  tableSortable: false,
  hasFixedColumnWidths: false,
  sortButtonsScreenReaderText: DEFAULT_SORT_BUTTONS_SCREEN_READER_TEXT,
  render: null,
  rowHeaderColumnKey: null,
  defaultSortedColumn: '',
  defaultSortDirection: '',
  hideHeader: false,
  isSelectable: false,
  selected: [],
  setSelected: () => { },
  isLoading: false,
  showRows: Infinity
};

export default withDeprecatedProps(Table, 'TableDeprecated', {
  className: {
    deprType: DeprTypes.FORMAT,
    expect: value => typeof value === 'string',
    transform: value => (Array.isArray(value) ? value.join(' ') : value),
    message: 'It should be a string.',
  },
});
